/* eslint-disable react/no-array-index-key */

import { makeStyles, Paper, Table, TableBody, TableCell, TableContainer, TableRow } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { WheelDTO } from 'src/modules/generated/api';
import { axles, getLabel, rimTypes, sides, tireTypes } from 'src/modules/labels';

import CarDetailHeadline from './CarDetailHeadline';
import CarDetailTableHeadCell from './CarDetailTableHeadCell';
import UnitValue from './UnitValue';

type CarDetailWheelProps = {
  wheels?: WheelDTO[];
  disableElevation?: boolean;
};

const useStyles = makeStyles((theme) => ({
  root: {
    '@media screen': {
      height: '100%',
    },
    '@media print': {
      overflow: 'visible',
      '& table': {
        fontSize: '9px',
        width: '100%',
      },
      '& td, & th': {
        padding: '2px 4px',
      },
    },
  },
  disableElevation: {
    borderRadius: 0,
  },
  headingBox: {
    padding: theme.spacing(2),
  },
}));

const CarDetailWheel = ({ wheels, disableElevation = false }: CarDetailWheelProps) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const wheelNumbersPresent = wheels?.some((wheel) => wheel.wheelNumber !== undefined) ?? false;
  const wheelAxlesPresent = wheels?.some((wheel) => wheel.axle !== undefined) ?? false;
  const wheelSidesPresent = wheels?.some((wheel) => wheel.side !== undefined) ?? false;
  const wheelSizesPresent = wheels?.some((wheel) => wheel.size !== undefined) ?? false;
  const wheelProfileDepthsPresent = wheels?.some((wheel) => wheel.profileDepth !== undefined) ?? false;
  const wheelProfileDepthsDetailPresent = wheels?.some((wheel) => wheel.profileDepthDetailed !== undefined) ?? false;
  const wheelTireTypesPresent = wheels?.some((wheel) => wheel.tireType !== undefined) ?? false;
  const wheelRimTypesPresent = wheels?.some((wheel) => wheel.rimType !== undefined) ?? false;
  const wheelManufacturersPresent = wheels?.some((wheel) => wheel.manufacturer !== undefined) ?? false;

  return (
    <TableContainer component={Paper} className={classes.root} elevation={disableElevation ? 0 : 3}>
      <div className={classes.headingBox}>
        <CarDetailHeadline title={t('car.wheels')} />
      </div>
      <Table size="small">
        <TableBody>
          {wheelNumbersPresent && (
            <TableRow>
              <CarDetailTableHeadCell className="bigger">{t('car.wheelNumber')}</CarDetailTableHeadCell>
              {wheels?.map((w, idx) => (
                <TableCell key={`wheelNumber-${idx}`}>
                  <span>{w.wheelNumber}</span>
                </TableCell>
              ))}
            </TableRow>
          )}
          {wheelAxlesPresent && (
            <TableRow>
              <CarDetailTableHeadCell className="bigger">{t('car.wheelAxle')}</CarDetailTableHeadCell>
              {wheels?.map((w, idx) => (
                <TableCell key={`axle-${idx}`}>
                  <span>{getLabel(axles, w.axle ?? '')}</span>
                </TableCell>
              ))}
            </TableRow>
          )}
          {wheelSidesPresent && (
            <TableRow>
              <CarDetailTableHeadCell className="bigger">{t('car.wheelSide')}</CarDetailTableHeadCell>
              {wheels?.map((w, idx) => (
                <TableCell key={`side-${idx}`}>
                  <span>{getLabel(sides, w.side ?? '')}</span>
                </TableCell>
              ))}
            </TableRow>
          )}
          {wheelSizesPresent && (
            <TableRow>
              <CarDetailTableHeadCell className="bigger">{t('car.wheelSize')}</CarDetailTableHeadCell>
              {wheels?.map((w, idx) => (
                <TableCell key={`size-${idx}`}>
                  <span>{w.size}</span>
                </TableCell>
              ))}
            </TableRow>
          )}
          {(wheelProfileDepthsPresent || wheelProfileDepthsDetailPresent) && (
            <TableRow>
              <CarDetailTableHeadCell className="bigger">{t('car.wheelProfileDepth')}</CarDetailTableHeadCell>
              {wheels?.map((w, idx) => (
                <TableCell key={`profileDepth-${idx}`}>
                  <UnitValue value={w.profileDepthDetailed ?? w.profileDepth} unit="mm" />
                </TableCell>
              ))}
            </TableRow>
          )}
          {wheelTireTypesPresent && (
            <TableRow>
              <CarDetailTableHeadCell className="bigger">{t('car.wheelTireType')}</CarDetailTableHeadCell>
              {wheels?.map((w, idx) => (
                <TableCell key={`tireType-${idx}`}>
                  <span>{getLabel(tireTypes, w.tireType ?? '')}</span>
                </TableCell>
              ))}
            </TableRow>
          )}
          {wheelRimTypesPresent && (
            <TableRow>
              <CarDetailTableHeadCell className="bigger">{t('car.wheelRimType')}</CarDetailTableHeadCell>
              {wheels?.map((w, idx) => (
                <TableCell key={`rimType-${idx}`}>
                  <span>{getLabel(rimTypes, w.rimType ?? '')}</span>
                </TableCell>
              ))}
            </TableRow>
          )}
          {wheelManufacturersPresent && (
            <TableRow>
              <CarDetailTableHeadCell className="bigger">{t('car.wheelManufacturer')}</CarDetailTableHeadCell>
              {wheels?.map((w, idx) => (
                <TableCell key={`manufacturer-${idx}`}>
                  <span>{w.manufacturer}</span>
                </TableCell>
              ))}
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default CarDetailWheel;
